import { useCallback, useEffect, useState } from "react";
import {
  addInboundFaxReviewCandidate,
  confirmInboundFaxReview,
  fetchInboundFaxReviewQueue,
  rejectInboundFaxReview,
} from "./inboundFaxReviewApi";

export default function useInboundFaxReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReviews(await fetchInboundFaxReviewQueue());
    } catch (err) {
      setError(err?.message || "Could not load the fax review queue.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decide = useCallback(async (reviewId, action, payload) => {
    setPendingId(reviewId);
    setError(null);
    try {
      await action(reviewId, payload);
      await load();
    } catch (err) {
      setError(err?.message || "The review could not be saved.");
      throw err;
    } finally {
      setPendingId(null);
    }
  }, [load]);

  const confirm = useCallback(
    (reviewId, payload) => decide(reviewId, confirmInboundFaxReview, payload),
    [decide],
  );

  const reject = useCallback(
    (reviewId, payload) => decide(reviewId, rejectInboundFaxReview, payload),
    [decide],
  );

  const addCandidate = useCallback(async (reviewId, residentId) => {
    setError(null);
    try {
      return await addInboundFaxReviewCandidate(reviewId, residentId);
    } catch (err) {
      setError(err?.message || "The resident could not be added to this fax.");
      throw err;
    }
  }, []);

  return {
    reviews,
    loading,
    error,
    pendingId,
    refresh: load,
    confirm,
    reject,
    addCandidate,
  };
}